
import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Poll } from '@/types/poll';
import { createQuestionsJson, transformSupabasePollToPoll } from '@/utils/polls/pollDataTransformers';

export const usePollSave = (
  polls: Poll[], 
  setPolls: React.Dispatch<React.SetStateAction<Poll[]>>, 
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const [isSaving, setIsSaving] = useState(false);

  const handleSavePoll = async (currentPoll: Poll | null, isEditing: boolean) => {
    if (!currentPoll) return;
    
    if (!currentPoll.title || !currentPoll.description) {
      toast.error('Title and description are required');
      return;
    }

    // Validate poll options
    if (currentPoll.options.length < 2) {
      toast.error('At least two options are required');
      return;
    }

    if (currentPoll.options.some(option => !option.text.trim())) {
      toast.error('All options must have text');
      return;
    }

    if (!currentPoll.category_id) {
      toast.error('A category must be selected');
      return;
    }
    
    try {
      setIsSaving(true);
      
      // Prepare the poll data for Supabase
      const questionsData = createQuestionsJson(currentPoll);

      // Convert status values to the allowed values
      let dbStatus: "draft" | "live" | "closed";
      
      if (currentPoll.status === 'live') {
        dbStatus = 'live';
      } else if (currentPoll.status === 'closed') {
        dbStatus = 'closed';
      } else {
        dbStatus = 'draft';
      }

      // Prepare poll data with proper handling of null values - following exact same pattern as anonymous_allowed
      const pollData = {
        title: currentPoll.title,
        description: currentPoll.description,
        status: dbStatus,
        country_code: currentPoll.country_code || null,
        constituency_code: currentPoll.constituency_code || null,
        closes_at: currentPoll.closes_at || null,
        questions: questionsData,
        category_id: currentPoll.category_id,
        image_url: currentPoll.image_url || null,
        anonymous_allowed: currentPoll.anonymous_allowed || false,
        public_voting_allowed: currentPoll.public_voting_allowed || false
      };

      console.log('Saving poll data:', pollData);

      let result;
      if (isEditing) {
        // Update existing poll
        try {
          // First try to update using PATCH
          result = await supabase
            .from('polls')
            .update(pollData)
            .eq('id', currentPoll.id)
            .select();
            
          if (result.error) {
            // If PATCH fails, try the older method without select
            console.warn('PATCH method failed, trying alternative approach:', result.error);
            
            const updateResult = await supabase
              .from('polls')
              .update(pollData)
              .eq('id', currentPoll.id);
              
            if (updateResult.error) {
              throw updateResult.error;
            }
            
            // If update succeeded but didn't return data, fetch the updated record
            const fetchResult = await supabase
              .from('polls')
              .select('*')
              .eq('id', currentPoll.id)
              .single();
              
            if (fetchResult.error) {
              throw fetchResult.error;
            }
            
            // Manually construct the result object
            result = {
              data: [fetchResult.data],
              error: null
            };
          }
        } catch (error) {
          console.error('Update error:', error);
          toast.error(`Failed to update poll: ${error.message || 'Unknown error'}`);
          return;
        }
        
        // Update local state
        if (result.data && result.data.length > 0) {
          const updatedPoll = transformSupabasePollToPoll(result.data[0]);
          setPolls(polls.map(poll => 
            poll.id === updatedPoll.id ? updatedPoll : poll
          ));
        } else {
          // If no data returned but update succeeded, update local state with current poll
          setPolls(polls.map(poll => 
            poll.id === currentPoll.id ? {
              ...currentPoll,
              status: currentPoll.status
            } : poll
          ));
        }
        toast.success('Poll updated successfully');
      } else {
        // Create new poll
        try {
          // Important: Wrap pollData in an array to fix the parameter binding issue
          result = await supabase
            .from('polls')
            .insert([pollData])
            .select();
          
          if (result.error) {
            console.error('Insert error:', result.error);
            toast.error(`Failed to create poll: ${result.error.message}`);
            return;
          }
          
          // Add new poll to local state
          if (result.data && result.data.length > 0) {
            const newPoll = transformSupabasePollToPoll(result.data[0]);
            setPolls([...polls, newPoll]);
            toast.success('New poll created successfully');
          }
        } catch (error: any) {
          console.error('Insert error:', error);
          toast.error(`Failed to create poll: ${error.message || 'Unknown error'}`);
          return;
        }
      }
      
      setDialogOpen(false);
    } catch (error: any) {
      console.error('Error saving poll:', error);
      toast.error(`An error occurred while saving the poll: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    handleSavePoll,
    isSaving
  };
};
